import React, { useEffect, useRef, useState, useMemo } from 'react';
import { GoogleMap, useJsApiLoader, Polyline } from '@react-google-maps/api';
import { LocateFixed } from 'lucide-react';
import { googleMapsLoaderConfig } from '../../config/googleMapsConfig';
import { uberMapStyle } from '../../styles/mapStyle';
import { computeRoute } from '../../utils/routesApi';
import {
  isDriverDeviated,
  resolveTargetDestination,
  calculateDistanceMeters,
  DEFAULT_DEVIATION_THRESHOLD_METERS,
  DEFAULT_RECALCULATION_COOLDOWN_MS,
  ARRIVAL_THRESHOLD_METERS,
} from '../../utils/routeDeviation';
import CommandCenterMapLegend from './CommandCenterMapLegend';
import ambulancePng from '../../svg/Rectangle.png';
import { makePinSvg, makePatientPinSvg } from '../../utils/mapMarkers';

const DEFAULT_CENTER = { lat: 23.0225, lng: 72.5714 }; // Ahmedabad

const toLatLng = (loc) => {
  if (!loc) return null;
  const lat = Number(loc.latitude ?? loc.lat);
  const lng = Number(loc.longitude ?? loc.lng ?? loc.lon);
  if (!isFinite(lat) || !isFinite(lng)) return null;
  return { lat, lng };
};

/**
 * Fetch driving directions using Google Maps DirectionsService directly in the browser,
 * with graceful fallback to computeRoute (Routes API REST).
 */
const fetchDrivingRoute = async (origin, destination) => {
  if (!origin || !destination) return [];

  // Try Google Maps JS SDK DirectionsService first (reliable in-browser, no CORS/fieldmask issues)
  if (window.google?.maps?.DirectionsService) {
    try {
      const directionsService = new window.google.maps.DirectionsService();
      const response = await new Promise((resolve, reject) => {
        directionsService.route(
          {
            origin: new window.google.maps.LatLng(origin.lat, origin.lng),
            destination: new window.google.maps.LatLng(destination.lat, destination.lng),
            travelMode: window.google.maps.TravelMode.DRIVING,
          },
          (result, status) => {
            if (status === window.google.maps.DirectionsStatus.OK && result) {
              resolve(result);
            } else {
              reject(new Error(`DirectionsService status: ${status}`));
            }
          }
        );
      });

      const overviewPath = response.routes?.[0]?.overview_path;
      if (overviewPath && overviewPath.length > 0) {
        return overviewPath.map((p) => ({ lat: p.lat(), lng: p.lng() }));
      }
    } catch (sdkErr) {
      console.warn('[CommandCenterTripLiveMap] DirectionsService failed, trying computeRoute:', sdkErr);
    }
  }

  // Fallback to computeRoute (Google Routes API REST)
  try {
    const { coordinates } = await computeRoute(origin, destination);
    if (coordinates && coordinates.length > 0) {
      return coordinates;
    }
  } catch (apiErr) {
    console.warn('[CommandCenterTripLiveMap] computeRoute failed:', apiErr);
  }

  return [origin, destination];
};

// ─── Smooth ambulance animation helper ───────────────────────────────────────
const animateMarker = (marker, prevPos, nextPos, durationMs = 1200) => {
  if (!marker) return () => {};

  const dLat = nextPos.lat - prevPos.lat;
  const dLng = nextPos.lng - prevPos.lng;
  if (dLat * dLat + dLng * dLng < 1e-12) {
    marker.setPosition(nextPos);
    return () => {};
  }

  let start = null;
  let rafId = null;
  let cancelled = false;

  const step = (timestamp) => {
    if (cancelled) return;
    if (!start) start = timestamp;
    const t = Math.min((timestamp - start) / durationMs, 1);
    const ease = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    marker.setPosition({
      lat: prevPos.lat + dLat * ease,
      lng: prevPos.lng + dLng * ease,
    });
    if (t < 1) {
      rafId = requestAnimationFrame(step);
    }
  };

  rafId = requestAnimationFrame(step);

  return () => {
    cancelled = true;
    if (rafId != null) cancelAnimationFrame(rafId);
    marker?.setPosition(nextPos);
  };
};

/**
 * CommandCenterTripLiveMap
 * 
 * Live Tracking Map with:
 * - Dynamic Rerouting when ambulance deviates from planned route (e.g. takes shortcuts)
 * - Original Planned Route in RED (#EF4444)
 * - Actual Traveled & Active Route in BLUE (#1A73E8) with direction arrows
 * - Live breadcrumb tracking of traveled ambulance path
 */
const CommandCenterTripLiveMap = ({
  pickupLocation,
  dropoffLocation,
  ambulanceLocation,
  patientLocation = null,
  stage,
  height = '100%',
  showLegend = true,
}) => {
  const { isLoaded } = useJsApiLoader({ ...googleMapsLoaderConfig });
  const [map, setMap] = useState(null);

  // Planned original route (RED)
  const [plannedRoute, setPlannedRoute] = useState([]);
  // Active route ahead to destination (BLUE with arrows)
  const [activeRoute, setActiveRoute] = useState([]);
  // Actual path traveled by ambulance in this tracking session (BLUE)
  const [traveledPath, setTraveledPath] = useState([]);
  const [isDeviated, setIsDeviated] = useState(false);

  const lastRecalculationRef = useRef(0);
  const isRecalculatingRef = useRef(false);

  const pickupMarkerRef = useRef(null);
  const dropoffMarkerRef = useRef(null);
  const ambulanceMarkerRef = useRef(null);
  const patientMarkerRef = useRef(null);
  const patientCircleRef = useRef(null);

  const initialBoundsFittedRef = useRef(false);
  const userInteractedRef = useRef(false);
  const isProgrammaticMoveRef = useRef(false);
  const mapContainerRef = useRef(null);

  const prevAmbulancePosRef = useRef(null);
  const cancelCurrentTweenRef = useRef(null);
  const ambulanceLocationRef = useRef(ambulanceLocation);
  ambulanceLocationRef.current = ambulanceLocation;

  // Memoize coordinates to prevent re-renders when parent creates new object wrappers
  const pickupPos = useMemo(() => toLatLng(pickupLocation), [
    pickupLocation?.lat,
    pickupLocation?.latitude,
    pickupLocation?.lng,
    pickupLocation?.longitude,
  ]);

  const dropoffPos = useMemo(() => toLatLng(dropoffLocation), [
    dropoffLocation?.lat,
    dropoffLocation?.latitude,
    dropoffLocation?.lng,
    dropoffLocation?.longitude,
  ]);

  const ambulancePos = useMemo(() => toLatLng(ambulanceLocation), [
    ambulanceLocation?.lat,
    ambulanceLocation?.latitude,
    ambulanceLocation?.lng,
    ambulanceLocation?.longitude,
  ]);

  const onMapLoad = (mapInstance) => {
    initialBoundsFittedRef.current = false;
    setMap(mapInstance);
  };

  // 1. Initial Planned Route (Pickup to Drop-off) in RED
  // Only re-calculates when pickup or dropoff coordinates actually change
  useEffect(() => {
    if (!pickupPos || !dropoffPos) {
      setPlannedRoute([]);
      setActiveRoute([]);
      return undefined;
    }
    let cancelled = false;

    fetchDrivingRoute(pickupPos, dropoffPos)
      .then((coords) => {
        if (cancelled) return;
        const validCoords = coords && coords.length > 0 ? coords : [pickupPos, dropoffPos];
        setPlannedRoute(validCoords);
        setActiveRoute((prev) => (prev.length === 0 ? validCoords : prev));
      })
      .catch(() => {
        if (!cancelled) {
          setPlannedRoute([pickupPos, dropoffPos]);
          setActiveRoute((prev) => (prev.length === 0 ? [pickupPos, dropoffPos] : prev));
        }
      });

    return () => { cancelled = true; };
  }, [pickupPos?.lat, pickupPos?.lng, dropoffPos?.lat, dropoffPos?.lng]);

  // 2. Traveled breadcrumbs tracking:
  // Records the real historical path traveled by the ambulance in this session
  useEffect(() => {
    if (!ambulancePos) return;

    setTraveledPath((prev) => {
      if (prev.length === 0) return [ambulancePos];
      const last = prev[prev.length - 1];
      const dist = calculateDistanceMeters(last.lat, last.lng, ambulancePos.lat, ambulancePos.lng);
      // Append if ambulance moved at least 5 meters
      if (dist >= 5) {
        return [...prev, ambulancePos];
      }
      return prev;
    });
  }, [ambulancePos?.lat, ambulancePos?.lng]);

  // 3. Dynamic Route Deviation Detection & Recalculation:
  // When driver deviates from the planned route (taking a shortcut or alternative path),
  // immediately recalculate the route from current ambulance position to target destination.
  useEffect(() => {
    if (!ambulancePos || !plannedRoute || plannedRoute.length < 2) return undefined;

    const target = resolveTargetDestination(stage, pickupPos, dropoffPos);
    if (!target) return undefined;

    // Skip recalculation if already arrived within arrival threshold
    const distToTarget = calculateDistanceMeters(ambulancePos.lat, ambulancePos.lng, target.lat, target.lng);
    if (distToTarget <= ARRIVAL_THRESHOLD_METERS) return undefined;

    const referenceRoute = activeRoute.length > 1 ? activeRoute : plannedRoute;
    const deviated = isDriverDeviated(
      { lat: ambulancePos.lat, lng: ambulancePos.lng, accuracy: ambulanceLocation?.accuracy },
      referenceRoute,
      DEFAULT_DEVIATION_THRESHOLD_METERS
    );

    // Also check deviation against planned route to show deviation badge
    const deviatedFromPlanned = isDriverDeviated(
      { lat: ambulancePos.lat, lng: ambulancePos.lng, accuracy: ambulanceLocation?.accuracy },
      plannedRoute,
      DEFAULT_DEVIATION_THRESHOLD_METERS
    );
    if (deviatedFromPlanned !== isDeviated) {
      setIsDeviated(deviatedFromPlanned);
    }

    if (!deviated) return undefined;

    const now = Date.now();
    if (now - lastRecalculationRef.current < DEFAULT_RECALCULATION_COOLDOWN_MS) return undefined;
    if (isRecalculatingRef.current) return undefined;

    console.log('🔄 [CommandCenterTripLiveMap] Route deviation detected — recalculating route from current ambulance position to target destination...');
    isRecalculatingRef.current = true;
    lastRecalculationRef.current = now;

    let cancelled = false;

    fetchDrivingRoute(ambulancePos, target)
      .then((coords) => {
        if (cancelled) return;
        if (coords && coords.length > 0) {
          console.log(`✅ [CommandCenterTripLiveMap] Rerouted successfully! New path has ${coords.length} points.`);
          setActiveRoute(coords);
          setIsDeviated(true);
        }
      })
      .catch((err) => {
        console.warn('⚠️ [CommandCenterTripLiveMap] Failed to recalculate route:', err);
      })
      .finally(() => {
        isRecalculatingRef.current = false;
      });

    return () => { cancelled = true; };
  }, [
    ambulancePos?.lat,
    ambulancePos?.lng,
    activeRoute,
    plannedRoute,
    stage,
    pickupPos,
    dropoffPos,
    ambulanceLocation?.accuracy,
    isDeviated,
  ]);

  // 4. Pickup marker (Blue pin) & Dropoff marker (Red pin)
  useEffect(() => {
    if (!map || !window.google?.maps) return undefined;

    if (pickupPos) {
      pickupMarkerRef.current = new window.google.maps.Marker({
        map,
        position: pickupPos,
        title: 'Pickup Location',
        icon: {
          url: makePinSvg('blue'),
          scaledSize: new window.google.maps.Size(26, 38),
          anchor: new window.google.maps.Point(13, 38),
        },
        zIndex: 900,
        optimized: false,
      });
    }
    if (dropoffPos) {
      dropoffMarkerRef.current = new window.google.maps.Marker({
        map,
        position: dropoffPos,
        title: 'Drop-off Location',
        icon: {
          url: makePinSvg('red'),
          scaledSize: new window.google.maps.Size(26, 38),
          anchor: new window.google.maps.Point(13, 38),
        },
        zIndex: 900,
        optimized: false,
      });
    }

    return () => {
      pickupMarkerRef.current?.setMap(null);
      dropoffMarkerRef.current?.setMap(null);
      pickupMarkerRef.current = null;
      dropoffMarkerRef.current = null;
    };
  }, [map, pickupPos?.lat, pickupPos?.lng, dropoffPos?.lat, dropoffPos?.lng]);

  // 5. Patient marker (if shared)
  const patientPos = patientLocation ? toLatLng(patientLocation) : null;
  const patientLat = patientPos ? patientPos.lat : null;
  const patientLng = patientPos ? patientPos.lng : null;
  const patientAccuracyM = patientLocation?.accuracyM;

  useEffect(() => {
    if (!map || !window.google?.maps || patientLat == null || patientLng == null) return undefined;

    const position = { lat: patientLat, lng: patientLng };
    patientMarkerRef.current = new window.google.maps.Marker({
      map,
      position,
      title: 'Patient (shared location)',
      icon: {
        url: makePatientPinSvg(),
        scaledSize: new window.google.maps.Size(40, 52),
        anchor: new window.google.maps.Point(20, 49),
      },
      zIndex: 910,
      optimized: false,
    });

    const accuracy = Number(patientAccuracyM);
    if (isFinite(accuracy) && accuracy > 0 && accuracy <= 2000) {
      patientCircleRef.current = new window.google.maps.Circle({
        map,
        center: position,
        radius: accuracy,
        strokeColor: '#0B7A66',
        strokeOpacity: 0.5,
        strokeWeight: 1,
        fillColor: '#0B7A66',
        fillOpacity: 0.12,
        clickable: false,
        zIndex: 5,
      });
    }

    return () => {
      patientMarkerRef.current?.setMap(null);
      patientCircleRef.current?.setMap(null);
      patientMarkerRef.current = null;
      patientCircleRef.current = null;
    };
  }, [map, patientLat, patientLng, patientAccuracyM]);

  // 6. Ambulance marker — smooth-animated on position updates
  useEffect(() => {
    if (!map || !window.google?.maps) return undefined;

    if (ambulancePos) {
      if (!ambulanceMarkerRef.current) {
        ambulanceMarkerRef.current = new window.google.maps.Marker({
          map,
          position: ambulancePos,
          title: 'Ambulance',
          icon: {
            url: ambulancePng,
            scaledSize: new window.google.maps.Size(44, 44),
            anchor: new window.google.maps.Point(22, 22),
          },
          zIndex: 920,
          optimized: false,
        });
        prevAmbulancePosRef.current = ambulancePos;
      } else {
        cancelCurrentTweenRef.current?.();
        const from = prevAmbulancePosRef.current ?? ambulancePos;
        prevAmbulancePosRef.current = ambulancePos;
        ambulanceMarkerRef.current.setMap(map);
        cancelCurrentTweenRef.current = animateMarker(
          ambulanceMarkerRef.current,
          from,
          ambulancePos,
          1200
        );
      }
    } else {
      cancelCurrentTweenRef.current?.();
      cancelCurrentTweenRef.current = null;
      ambulanceMarkerRef.current?.setMap(null);
      ambulanceMarkerRef.current = null;
      prevAmbulancePosRef.current = null;
    }

    return () => {
      cancelCurrentTweenRef.current?.();
      cancelCurrentTweenRef.current = null;
      ambulanceMarkerRef.current?.setMap(null);
      ambulanceMarkerRef.current = null;
      prevAmbulancePosRef.current = null;
    };
  }, [map, ambulancePos?.lat, ambulancePos?.lng]);

  // 7. Initial fitBounds — runs once when map + locations ready
  useEffect(() => {
    if (!map || !window.google?.maps) return;
    if (initialBoundsFittedRef.current) return;

    const points = [];
    if (pickupPos) points.push(pickupPos);
    if (dropoffPos) points.push(dropoffPos);
    const ambPt = toLatLng(ambulanceLocationRef.current);
    if (ambPt) points.push(ambPt);

    const id = setTimeout(() => {
      isProgrammaticMoveRef.current = true;
      if (points.length === 0) {
        map.setCenter(DEFAULT_CENTER);
        map.setZoom(12);
      } else if (points.length === 1) {
        map.setCenter(points[0]);
        map.setZoom(14);
      } else {
        const bounds = new window.google.maps.LatLngBounds();
        points.forEach((p) => {
          if (p && isFinite(p.lat) && isFinite(p.lng)) {
            bounds.extend(p);
          }
        });
        map.fitBounds(bounds, { padding: 60 });
      }
      initialBoundsFittedRef.current = true;
      setTimeout(() => {
        isProgrammaticMoveRef.current = false;
      }, 400);
    }, 60);
    return () => clearTimeout(id);
  }, [map, pickupPos?.lat, pickupPos?.lng, dropoffPos?.lat, dropoffPos?.lng]);

  // 8. Patient location arrived later bounds adjustment
  useEffect(() => {
    if (!map || !window.google?.maps || patientLat == null || patientLng == null) return undefined;
    if (userInteractedRef.current) return undefined;

    const bounds = new window.google.maps.LatLngBounds();
    [pickupPos, dropoffPos, toLatLng(ambulanceLocationRef.current), { lat: patientLat, lng: patientLng }]
      .forEach((p) => { if (p) bounds.extend(p); });
    isProgrammaticMoveRef.current = true;
    map.fitBounds(bounds, { padding: 60 });
    const id = setTimeout(() => { isProgrammaticMoveRef.current = false; }, 400);
    return () => clearTimeout(id);
  }, [map, patientLat, patientLng, pickupPos, dropoffPos]);

  const handleRecenter = () => {
    userInteractedRef.current = false;
    if (!map || !window.google?.maps) return;
    window.google.maps.event.trigger(map, 'resize');

    const ambPt = toLatLng(ambulanceLocationRef.current);
    const points = [];
    if (pickupPos) points.push(pickupPos);
    if (dropoffPos) points.push(dropoffPos);
    if (ambPt) points.push(ambPt);

    isProgrammaticMoveRef.current = true;
    if (ambPt) {
      map.panTo(ambPt);
      if (map.getZoom() < 14) {
        map.setZoom(15);
      }
    } else if (points.length > 1) {
      const bounds = new window.google.maps.LatLngBounds();
      points.forEach((p) => bounds.extend(p));
      map.fitBounds(bounds, { padding: 60 });
    }
    setTimeout(() => {
      isProgrammaticMoveRef.current = false;
    }, 600);
  };

  // Keep map tiles rendered when container resizes or scrolls (prevents blank/grey screen)
  useEffect(() => {
    if (!map) return undefined;
    const container = mapContainerRef.current;
    if (!container) return undefined;

    let rafId = null;
    const triggerResize = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        if (map && window.google?.maps) {
          window.google.maps.event.trigger(map, 'resize');
        }
      });
    };

    const resizeObserver = new ResizeObserver(() => {
      triggerResize();
    });
    resizeObserver.observe(container);

    window.addEventListener('scroll', triggerResize, { passive: true });
    const scrollParent = container.closest('.overflow-y-auto') || container.closest('main');
    scrollParent?.addEventListener('scroll', triggerResize, { passive: true });

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('scroll', triggerResize);
      scrollParent?.removeEventListener('scroll', triggerResize);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [map]);

  return (
    <div className="w-full h-full flex flex-col min-h-0" style={{ height }}>
      <div
        ref={mapContainerRef}
        style={{ willChange: 'transform', transform: 'translateZ(0)' }}
        className="flex-1 min-h-0 relative"
      >
        {isLoaded ? (
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '100%' }}
            onLoad={onMapLoad}
            onDragStart={() => {
              userInteractedRef.current = true;
            }}
            onZoomChanged={() => {
              if (!isProgrammaticMoveRef.current) {
                userInteractedRef.current = true;
              }
            }}
            options={{
              zoomControl: true,
              streetViewControl: false,
              mapTypeControl: false,
              fullscreenControl: false,
              styles: uberMapStyle,
            }}
          >
            {/* ── ROUTE 1: ORIGINAL PLANNED ROUTE (RED) ──────────────── */}
            {plannedRoute.length >= 2 && window.google?.maps && (
              <>
                {/* Planned route glow */}
                <Polyline
                  key={`planned-glow-${plannedRoute.length}`}
                  path={plannedRoute}
                  options={{
                    strokeColor: '#DC2626',
                    strokeOpacity: 0.25,
                    strokeWeight: 7,
                    geodesic: true,
                    zIndex: 880,
                  }}
                />
                {/* Main planned route line in RED */}
                <Polyline
                  key={`planned-main-${plannedRoute.length}`}
                  path={plannedRoute}
                  options={{
                    strokeColor: '#EF4444',
                    strokeOpacity: 0.85,
                    strokeWeight: 4,
                    geodesic: true,
                    zIndex: 885,
                    icons: [
                      {
                        icon: {
                          path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                          fillColor: '#EF4444',
                          fillOpacity: 1,
                          strokeColor: '#FFFFFF',
                          strokeWeight: 1.2,
                          scale: 2.5,
                        },
                        offset: '25px',
                        repeat: '70px',
                      },
                    ],
                  }}
                />
              </>
            )}

            {/* ── ROUTE 2: ACTUAL TRAVELED BREADCRUMBS (BLUE) ─────────── */}
            {traveledPath.length >= 2 && window.google?.maps && (
              <>
                <Polyline
                  key={`traveled-glow-${traveledPath.length}`}
                  path={traveledPath}
                  options={{
                    strokeColor: '#1E40AF',
                    strokeOpacity: 0.3,
                    strokeWeight: 8,
                    geodesic: true,
                    zIndex: 890,
                  }}
                />
                <Polyline
                  key={`traveled-main-${traveledPath.length}`}
                  path={traveledPath}
                  options={{
                    strokeColor: '#1A73E8',
                    strokeOpacity: 0.95,
                    strokeWeight: 4.5,
                    geodesic: true,
                    zIndex: 892,
                  }}
                />
              </>
            )}

            {/* ── ROUTE 3: ACTIVE REMAINING ROUTE TO DESTINATION (BLUE) ── */}
            {activeRoute.length >= 2 && window.google?.maps && (
              <>
                {/* Glow/Shadow line */}
                <Polyline
                  key={`active-glow-${activeRoute.length}`}
                  path={activeRoute}
                  options={{
                    strokeColor: '#1E40AF',
                    strokeOpacity: 0.25,
                    strokeWeight: 10,
                    geodesic: true,
                    zIndex: 895,
                  }}
                />
                {/* Main Blue Route with Forward Direction Arrows */}
                <Polyline
                  key={`active-main-${activeRoute.length}`}
                  path={activeRoute}
                  options={{
                    strokeColor: '#1A73E8',
                    strokeOpacity: 1,
                    strokeWeight: 5,
                    geodesic: true,
                    zIndex: 898,
                    icons: [
                      {
                        icon: {
                          path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                          fillColor: '#1A73E8',
                          fillOpacity: 1,
                          strokeColor: '#FFFFFF',
                          strokeWeight: 1.5,
                          scale: 3,
                        },
                        offset: '25px',
                        repeat: '65px',
                      },
                    ],
                  }}
                />
              </>
            )}
          </GoogleMap>
        ) : (
          <div className="w-full h-full bg-gray-100" />
        )}

        {/* Status Indicators in Top-Left */}
        <div className="absolute top-2 left-2 z-[5] flex items-center gap-2">
          {ambulanceLocation && (
            <div className="flex items-center gap-1.5 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-md shadow px-2.5 py-1">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              <span className="text-xs font-semibold text-gray-900">Live Location</span>
            </div>
          )}
          {isDeviated && (
            <div className="flex items-center gap-1.5 bg-amber-50/95 backdrop-blur-sm border border-amber-200 rounded-md shadow px-2.5 py-1 text-xs font-semibold text-amber-800 animate-in fade-in">
              <span className="w-2 h-2 rounded-full bg-amber-500" />
              <span>Route Deviation (Updated Path)</span>
            </div>
          )}
        </div>

        {/* Recenter Button in Top-Right */}
        <div className="absolute top-2 right-2 z-[5] flex items-center gap-1.5">
          <button
            type="button"
            onClick={handleRecenter}
            className="p-2 bg-white hover:bg-gray-50 rounded-lg shadow-md transition-colors border border-gray-200 text-gray-700 cursor-pointer"
            title="Re-center map to ambulance"
          >
            <LocateFixed className="h-4 w-4 text-gray-700" />
          </button>
        </div>
      </div>
      {showLegend && <CommandCenterMapLegend showPatient={patientLat != null} isDeviated={isDeviated} />}
    </div>
  );
};

export default CommandCenterTripLiveMap;
